import {
  Project,
  Thought,
  AboutData,
  ContactData,
  SocialLink,
  PageContent,
} from '../types'
import { PROJECTS, THOUGHTS, EXPERIENCES, TOOLS } from '../constants'
import { supabase } from './supabase'

// Helper: map Supabase snake_case row → Project type
const mapProject = (row: any): Project => ({
  id: String(row.id),
  slug: row.slug,
  title: row.title,
  description: row.description,
  longDescription: row.long_description,
  problem: row.problem,
  solution: row.solution,
  tech_stack: row.tech_stack,
  features: row.features,
  outcomes: row.outcomes,
  flow: row.flow,
  year: row.year,
  category: row.category,
  coverImage:
    row.coverimage ||
    row.cover_image ||
    row.thumbnail ||
    (row.gallery?.[0] ?? ''),
  videoUrl: row.videourl || row.video_url || '',
  gallery: row.gallery ?? [],
  accentColor: row.accentcolor ?? row.accent_color ?? '#1A1A1A',
  status: row.status ?? 'published',
  tags: row.tags ?? [],
  client: row.client,
})

export const ContentService = {
  getAllProjects: async (): Promise<Project[]> => {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .order('id', { ascending: true })

    console.log('[Supabase] getAllProjects →', {
      count: data?.length,
      error: error?.message,
      errorCode: error?.code,
    })

    // Debug: log raw p08 row to see exact column names and values
    const p08 = data?.find(
      (r: any) => String(r.id) === '8' || r.slug?.includes('p08'),
    )
    if (p08) {
      console.log('[DEBUG] p08 raw row →', JSON.stringify(p08, null, 2))
    }

    if (error || !data || data.length === 0) {
      console.warn(
        '[Supabase] Falling back to mock data. Reason:',
        error?.message ?? 'empty result',
      )
      return PROJECTS
    }

    return data.map(mapProject)
  },

  getProjectBySlug: async (slug: string): Promise<Project | undefined> => {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('slug', slug)
      .single()

    if (error || !data) {
      console.warn('Supabase error, falling back to mock data.', error?.message)
      return PROJECTS.find((p) => p.slug === slug)
    }

    return mapProject(data)
  },

  getThoughts: async (): Promise<Thought[]> => {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .order('id', { ascending: true })

    console.log('[Supabase] getThoughts →', {
      count: data?.length,
      error: error?.message,
    })

    if (error || !data || data.length === 0) {
      console.warn(
        'Supabase getThoughts error, falling back to mock data.',
        error?.message ?? 'empty result',
      )
      return THOUGHTS
    }

    return data as Thought[]
  },

  getAboutData: async (): Promise<AboutData> => {
    const { data, error } = await supabase.from('about').select('*').single()

    if (error || !data) {
      console.warn('Supabase error, falling back to mock data.', error?.message)
      return {
        title: 'about',
        bio_hero: "i'm a product experience builder + design polymath",
        bio_detailed:
          'mixing UX, UI, prototypes, illustrations, motion, and storytelling to ship experiences that feel smooth, smart, and human.',
        profile_image:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1000&auto=format&fit=crop',
        location: 'Bengaluru',
        experience_years: '12+ years',
        availability_status: 'Building',
        experience_list: EXPERIENCES,
        tools_list: TOOLS,
      }
    }

    return {
      title: data.title ?? 'about',
      bio_hero: data.bio_hero,
      bio_detailed: data.bio_detailed,
      profile_image: data.profile_image,
      location: data.location,
      experience_years: data.experience_years,
      availability_status: data.availability_status,
      experience_list: data.experience_list ?? EXPERIENCES,
      tools_list: data.tools_list ?? TOOLS,
    }
  },

  getContactData: async (): Promise<ContactData> => {
    const { data, error } = await supabase.from('contact').select('*').single()

    if (error || !data) {
      console.warn('Supabase error, falling back to mock data.', error?.message)
      return {
        title: 'say hello',
        description:
          "let's collaborate. feel free to drop me a line about your project or follow me on social networks",
        email: 'shylash.k@gmail.com',
        whatsapp: '919000000000',
        availability_note: 'available for new opportunities',
        form_enabled: true,
      }
    }

    return data as ContactData
  },

  getSocialLinks: async (): Promise<SocialLink[]> => {
    const { data, error } = await supabase
      .from('social_links')
      .select('*')
      .order('sort_order', { ascending: true })

    if (error || !data) {
      console.warn('Supabase error, falling back to mock data.', error?.message)
      return [
        {
          platform: 'twitter',
          handle: '@shylash',
          url: 'https://twitter.com',
          icon: '🐦',
        },
        {
          platform: 'linkedin',
          handle: 'shylash-k',
          url: 'https://linkedin.com',
          icon: '💼',
        },
        {
          platform: 'github',
          handle: 'shylash',
          url: 'https://github.com',
          icon: '🐙',
        },
      ]
    }

    return data as SocialLink[]
  },

  getPageContent: async (slug: string): Promise<PageContent> => {
    const { data, error } = await supabase
      .from('page_content')
      .select('*')
      .eq('slug', slug)
      .single()

    if (error || !data) {
      console.warn('Supabase error, falling back to mock data.', error?.message)
      return {
        slug,
        title: 'Home',
        hero_title: 'Creation is my \ndopamine dealer 😌✨',
        hero_subtitle:
          "So I committed to the 100 Builds challenge to push my limits, build like crazy, and evolve into the 'new me' one project at a time.",
      }
    }

    return data as PageContent
  },

  submitContactMessage: async (data: any): Promise<boolean> => {
    const { error } = await supabase.from('contact_messages').insert([
      {
        name: data.name,
        email: data.email,
        message: data.message,
        created_at: new Date().toISOString(),
      },
    ])

    if (error) {
      console.error('Failed to submit message:', error.message)
      return false
    }

    return true
  },
}
